import React, { useState } from 'react';
import { ChevronLeft, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const AGE_GROUPS = [
  { id: '18-24', label: '18–24' },
  { id: '25-34', label: '25–34' },
  { id: '35-44', label: '35–44' },
  { id: '45-54', label: '45–54' },
  { id: '55+', label: '55+' },
];

const OCCUPATIONS = [
  { id: 'student', label: 'Student' },
  { id: 'professional', label: 'Working Professional' },
  { id: 'freelancer', label: 'Freelancer' },
  { id: 'homemaker', label: 'Homemaker' },
  { id: 'retired', label: 'Retired' },
  { id: 'other', label: 'Other' },
];

const ENERGY_PATTERNS = [
  { id: 'morning', label: 'Morning Person', desc: 'Most productive before noon' },
  { id: 'afternoon', label: 'Afternoon Peaker', desc: 'Hit my stride after lunch' },
  { id: 'evening', label: 'Night Owl', desc: 'Come alive in the evening' },
  { id: 'variable', label: 'Varies Day to Day', desc: 'No consistent pattern' },
];

export default function PersonalityOnboarding({ deviceId, onComplete, onBack }) {
  const [substep, setSubstep] = useState(0); // 0=age, 1=occupation, 2=energy
  const [ageGroup, setAgeGroup] = useState('');
  const [occupation, setOccupation] = useState('');
  const [energyPattern, setEnergyPattern] = useState('');
  const [saving, setSaving] = useState(false);

  const handleNext = async () => {
    if (substep < 2) {
      setSubstep(substep + 1);
      return;
    }
    setSaving(true);
    try {
      await api.post('/personality/onboarding', {
        device_id: deviceId,
        age_group: ageGroup,
        occupation,
        energy_pattern: energyPattern,
      });
      onComplete({ age_group: ageGroup, occupation, energy_pattern: energyPattern });
    } catch {}
    setSaving(false);
  };

  const canProceed = substep === 0 ? ageGroup : substep === 1 ? occupation : energyPattern;

  const titles = ['How old are you?', 'What do you do?', 'When are you most energetic?'];

  return (
    <div className="max-w-xl mx-auto animate-fade-in" data-testid="personality-onboarding">
      {/* Back */}
      <button onClick={substep > 0 ? () => setSubstep(substep - 1) : onBack}
        className="flex items-center gap-1 text-sm mb-6 transition-all hover:opacity-70"
        style={{ color: 'var(--primary)' }} data-testid="onboarding-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      {/* Progress */}
      <div className="flex gap-2 mb-8">
        {[0, 1, 2].map(i => (
          <div key={i} className="flex-1 h-1.5 rounded-full transition-all duration-300"
            style={{ background: i <= substep ? 'var(--primary)' : 'var(--muted)' }} />
        ))}
      </div>

      <p className="text-xs uppercase tracking-widest mb-2 font-semibold" style={{ fontFamily: 'Manrope', color: 'var(--muted-fg)' }}>
        Step {substep + 1} of 3
      </p>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
        {titles[substep]}
      </h2>

      {/* Age Group */}
      {substep === 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="age-group-options">
          {AGE_GROUPS.map(ag => (
            <button key={ag.id} onClick={() => setAgeGroup(ag.id)}
              className="p-4 rounded-lg border text-center font-medium text-sm transition-all hover:-translate-y-0.5"
              style={{
                background: ageGroup === ag.id ? 'var(--primary)' : 'var(--surface)',
                color: ageGroup === ag.id ? 'var(--primary-fg)' : 'var(--text)',
                borderColor: ageGroup === ag.id ? 'var(--primary)' : 'var(--border)',
              }}
              data-testid={`age-${ag.id}`}>
              {ag.label}
            </button>
          ))}
        </div>
      )}

      {/* Occupation */}
      {substep === 1 && (
        <div className="grid grid-cols-2 gap-3" data-testid="occupation-options">
          {OCCUPATIONS.map(oc => (
            <button key={oc.id} onClick={() => setOccupation(oc.id)}
              className="p-4 rounded-lg border text-left font-medium text-sm transition-all hover:-translate-y-0.5"
              style={{
                background: occupation === oc.id ? 'var(--primary)' : 'var(--surface)',
                color: occupation === oc.id ? 'var(--primary-fg)' : 'var(--text)',
                borderColor: occupation === oc.id ? 'var(--primary)' : 'var(--border)',
              }}
              data-testid={`occupation-${oc.id}`}>
              {oc.label}
            </button>
          ))}
        </div>
      )}

      {/* Energy Pattern */}
      {substep === 2 && (
        <div className="space-y-3" data-testid="energy-options">
          {ENERGY_PATTERNS.map(ep => (
            <button key={ep.id} onClick={() => setEnergyPattern(ep.id)}
              className="w-full p-4 rounded-lg border text-left transition-all hover:-translate-y-0.5"
              style={{
                background: energyPattern === ep.id ? 'var(--primary)' : 'var(--surface)',
                color: energyPattern === ep.id ? 'var(--primary-fg)' : 'var(--text)',
                borderColor: energyPattern === ep.id ? 'var(--primary)' : 'var(--border)',
              }}
              data-testid={`energy-${ep.id}`}>
              <p className="font-semibold text-sm">{ep.label}</p>
              <p className="text-xs mt-0.5 opacity-80">{ep.desc}</p>
            </button>
          ))}
        </div>
      )}

      {/* Next Button */}
      <button onClick={handleNext} disabled={!canProceed || saving} data-testid="onboarding-next"
        className="mt-8 w-full py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
        style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}>
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        {substep < 2 ? 'Continue' : 'Start Questions'}
      </button>
    </div>
  );
}
